/**
 * Client-side cutlist nesting optimiser for sheet products.
 *
 * Depends on:
 *  - window.CutlistProductData (rendered by snippets/product-cutlist-optimiser.liquid):
 *      { variants: [{id, price, thickness, length, width}], selectedVariantId, machiningPricing, labels }
 *  - window.MachiningChargeTable (rendered by snippets/machining-charge-table.liquid)
 *  - window.MachiningCharge (assets/machining-charge.js)
 */
(function () {
  'use strict';

  var BADGE_COLORS = ['#16A34A', '#2563EB', '#F59E0B', '#DC2626', '#7C3AED', '#0891B2', '#DB2777', '#65A30D'];

  function badgeForIndex(index) {
    var letter = String.fromCharCode(65 + (index % 26));
    if (index >= 26) letter += Math.floor(index / 26);
    return { letter: letter, color: BADGE_COLORS[index % BADGE_COLORS.length] };
  }

  // ---------------------------------------------------------------------
  // MaxRects (Best Short Side Fit) bin packer, with 90-degree rotation.
  // ---------------------------------------------------------------------

  function createPacker(width, height) {
    return {
      width: width,
      height: height,
      freeRects: [{ x: 0, y: 0, w: width, h: height }],
    };
  }

  function splitFreeRect(freeRect, placed) {
    var results = [];

    if (
      placed.x >= freeRect.x + freeRect.w ||
      placed.x + placed.w <= freeRect.x ||
      placed.y >= freeRect.y + freeRect.h ||
      placed.y + placed.h <= freeRect.y
    ) {
      return [freeRect];
    }

    if (placed.x > freeRect.x) {
      results.push({ x: freeRect.x, y: freeRect.y, w: placed.x - freeRect.x, h: freeRect.h });
    }
    if (placed.x + placed.w < freeRect.x + freeRect.w) {
      results.push({
        x: placed.x + placed.w,
        y: freeRect.y,
        w: freeRect.x + freeRect.w - (placed.x + placed.w),
        h: freeRect.h,
      });
    }
    if (placed.y > freeRect.y) {
      results.push({ x: freeRect.x, y: freeRect.y, w: freeRect.w, h: placed.y - freeRect.y });
    }
    if (placed.y + placed.h < freeRect.y + freeRect.h) {
      results.push({
        x: freeRect.x,
        y: placed.y + placed.h,
        w: freeRect.w,
        h: freeRect.y + freeRect.h - (placed.y + placed.h),
      });
    }

    return results;
  }

  function isContainedIn(a, b) {
    return a.x >= b.x && a.y >= b.y && a.x + a.w <= b.x + b.w && a.y + a.h <= b.y + b.h;
  }

  function pruneFreeList(freeRects) {
    var kept = [];
    for (var i = 0; i < freeRects.length; i++) {
      var contained = false;
      for (var j = 0; j < freeRects.length; j++) {
        if (i === j) continue;
        if (isContainedIn(freeRects[i], freeRects[j])) {
          if (isContainedIn(freeRects[j], freeRects[i]) && j < i) continue;
          contained = true;
          break;
        }
      }
      if (!contained) kept.push(freeRects[i]);
    }
    return kept;
  }

  /**
   * Places `piece` ({w, h}, its true requested size) into the packer using
   * Best Short Side Fit. `kerf` (mm) is reserved as extra clearance beyond
   * the piece's own footprint when carving up the remaining free space, so
   * later pieces placed in that leftover area end up at least one kerf away
   * - but the reservation is clamped to the free rect the piece was placed
   * in, so it never eats into space beyond what was already free (i.e. a
   * piece sized to exactly fill the sheet's width/height is never rejected
   * for "needing" clearance past the sheet's own edge).
   */
  function insert(packer, piece, kerf) {
    kerf = kerf || 0;
    var best = null;

    for (var i = 0; i < packer.freeRects.length; i++) {
      var free = packer.freeRects[i];

      var orientations = [{ w: piece.w, h: piece.h, rotated: false }];
      if (piece.w !== piece.h) {
        orientations.push({ w: piece.h, h: piece.w, rotated: true });
      }

      for (var o = 0; o < orientations.length; o++) {
        var ori = orientations[o];
        if (ori.w > free.w + 1e-9 || ori.h > free.h + 1e-9) continue;

        var leftoverX = free.w - ori.w;
        var leftoverY = free.h - ori.h;
        var shortSide = Math.min(leftoverX, leftoverY);
        var longSide = Math.max(leftoverX, leftoverY);

        if (!best || shortSide < best.shortSide - 1e-9 || (Math.abs(shortSide - best.shortSide) < 1e-9 && longSide < best.longSide)) {
          best = {
            x: free.x,
            y: free.y,
            w: ori.w,
            h: ori.h,
            rotated: ori.rotated,
            shortSide: shortSide,
            longSide: longSide,
            freeW: free.w,
            freeH: free.h,
          };
        }
      }
    }

    if (!best) return null;

    var footprint = {
      x: best.x,
      y: best.y,
      w: Math.min(best.w + kerf, best.freeW),
      h: Math.min(best.h + kerf, best.freeH),
    };
    var newFree = [];
    for (var f = 0; f < packer.freeRects.length; f++) {
      var pieces = splitFreeRect(packer.freeRects[f], footprint);
      for (var p = 0; p < pieces.length; p++) newFree.push(pieces[p]);
    }
    packer.freeRects = pruneFreeList(newFree);

    return { x: best.x, y: best.y, w: best.w, h: best.h, rotated: best.rotated };
  }

  /**
   * Packs a list of pieces ({w, h, qty, badge}) into as many sheets of
   * sheetWidth x sheetHeight as needed. `kerf` (mm) is the width of material
   * the saw blade removes per cut: it's passed through to insert() as extra
   * clearance reserved between neighbouring pieces, but every placed piece
   * is still reported at its true requested size - kerf only affects how
   * tightly pieces can be nested, not their dimensions or the
   * cut-length/machining-charge calculation.
   *
   * Returns { sheets: [{width, height, pieces:[{x,y,w,h,badge}], freeRects:[{x,y,w,h}]}] }
   * or { error: 'piece-too-large', piece } if something can't fit at all.
   */
  function packPieces(pieces, sheetWidth, sheetHeight, kerf) {
    kerf = kerf || 0;

    var units = [];
    for (var i = 0; i < pieces.length; i++) {
      var piece = pieces[i];
      for (var q = 0; q < piece.qty; q++) {
        units.push({ w: piece.width, h: piece.height, badge: piece.badge });
      }
    }

    units.sort(function (a, b) {
      return b.w * b.h - a.w * a.h;
    });

    var sheets = [];
    var packers = [];

    for (var u = 0; u < units.length; u++) {
      var unit = units[u];

      if (!fitsEither(unit, sheetWidth, sheetHeight)) {
        return { error: 'piece-too-large', piece: unit };
      }

      var currentPacker = packers[packers.length - 1];
      var placement = currentPacker ? insert(currentPacker, unit, kerf) : null;

      if (!placement) {
        currentPacker = createPacker(sheetWidth, sheetHeight);
        packers.push(currentPacker);
        sheets.push({ width: sheetWidth, height: sheetHeight, pieces: [] });
        placement = insert(currentPacker, unit, kerf);
        if (!placement) {
          return { error: 'piece-too-large', piece: unit };
        }
      }

      var sheet = sheets[sheets.length - 1];
      sheet.pieces.push({
        x: placement.x,
        y: placement.y,
        w: placement.w,
        h: placement.h,
        badge: unit.badge,
      });
    }

    for (var s = 0; s < sheets.length; s++) {
      sheets[s].freeRects = packers[s].freeRects.slice();
    }

    return { sheets: sheets };
  }

  function fitsEither(unit, sheetWidth, sheetHeight) {
    var fitsAsIs = unit.w <= sheetWidth + 1e-9 && unit.h <= sheetHeight + 1e-9;
    var fitsRotated = unit.h <= sheetWidth + 1e-9 && unit.w <= sheetHeight + 1e-9;
    return fitsAsIs || fitsRotated;
  }

  function largestFreeRect(freeRects) {
    var largest = null;
    for (var i = 0; i < freeRects.length; i++) {
      var r = freeRects[i];
      if (!largest || r.w * r.h > largest.w * largest.h) largest = r;
    }
    return largest;
  }

  // ---------------------------------------------------------------------
  // SVG rendering
  // ---------------------------------------------------------------------

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var SVG_MAX_PX = 360;
  var OFFCUT_MIN_AREA_RATIO = 0.03;

  function renderSheetSvg(sheet, offcutLabel) {
    var scale = SVG_MAX_PX / Math.max(sheet.width, sheet.height);
    var pxW = sheet.width * scale;
    var pxH = sheet.height * scale;

    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'cutlist__sheet-plan');
    svg.setAttribute('viewBox', '0 0 ' + pxW + ' ' + pxH);
    svg.setAttribute('width', pxW);
    svg.setAttribute('height', pxH);

    for (var i = 0; i < sheet.pieces.length; i++) {
      var p = sheet.pieces[i];

      var rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x', p.x * scale);
      rect.setAttribute('y', p.y * scale);
      rect.setAttribute('width', p.w * scale);
      rect.setAttribute('height', p.h * scale);
      rect.setAttribute('rx', 4);
      rect.setAttribute('fill', p.badge.color);
      svg.appendChild(rect);

      var label = document.createElementNS(SVG_NS, 'text');
      label.setAttribute('class', 'cutlist__piece-label');
      label.setAttribute('x', (p.x + p.w / 2) * scale);
      label.setAttribute('y', (p.y + p.h / 2) * scale);
      label.textContent = p.badge.letter + ' ' + Math.round(p.w) + '×' + Math.round(p.h);
      svg.appendChild(label);
    }

    var sheetArea = sheet.width * sheet.height;
    var offcut = largestFreeRect(sheet.freeRects);
    if (offcut && offcut.w * offcut.h >= sheetArea * OFFCUT_MIN_AREA_RATIO) {
      var offcutRect = document.createElementNS(SVG_NS, 'rect');
      offcutRect.setAttribute('class', 'cutlist__offcut-rect');
      offcutRect.setAttribute('x', offcut.x * scale + 1);
      offcutRect.setAttribute('y', offcut.y * scale + 1);
      offcutRect.setAttribute('width', Math.max(offcut.w * scale - 2, 0));
      offcutRect.setAttribute('height', Math.max(offcut.h * scale - 2, 0));
      offcutRect.setAttribute('rx', 4);
      svg.appendChild(offcutRect);

      var offcutText = document.createElementNS(SVG_NS, 'text');
      offcutText.setAttribute('class', 'cutlist__offcut-label');
      offcutText.setAttribute('x', (offcut.x + offcut.w / 2) * scale);
      offcutText.setAttribute('y', (offcut.y + offcut.h / 2) * scale);
      offcutText.textContent = offcutLabel + ' ' + Math.round(offcut.w) + '×' + Math.round(offcut.h);
      svg.appendChild(offcutText);
    }

    return svg;
  }

  function sheetUsedPercent(sheet) {
    var used = 0;
    for (var i = 0; i < sheet.pieces.length; i++) {
      used += sheet.pieces[i].w * sheet.pieces[i].h;
    }
    return Math.round((used / (sheet.width * sheet.height)) * 100);
  }

  // ---------------------------------------------------------------------
  // UI wiring
  // ---------------------------------------------------------------------

  function init() {
    var root = document.querySelector('[data-cutlist-root]');
    if (!root || !window.CutlistProductData) return;

    var data = window.CutlistProductData;
    var labels = data.labels || {};
    var variantsById = {};
    for (var i = 0; i < data.variants.length; i++) variantsById[data.variants[i].id] = data.variants[i];

    var currentVariantId = data.selectedVariantId;
    var lastResult = null;
    var recalcTimer = null;

    document.addEventListener('variant:changed', function (event) {
      if (event.detail && event.detail.variant) {
        currentVariantId = event.detail.variant.id;
        scheduleRecalculate();
      }
    });

    var termsEl = root.querySelector('[data-cutlist-terms]');
    var mainEl = root.querySelector('[data-cutlist-main]');
    var termsAcceptButton = root.querySelector('[data-cutlist-terms-accept]');

    function showTerms() {
      if (termsEl) termsEl.hidden = false;
      if (mainEl) mainEl.hidden = true;
    }

    function acceptTerms() {
      if (termsEl) termsEl.hidden = true;
      if (mainEl) mainEl.hidden = false;
    }

    if (termsAcceptButton) {
      termsAcceptButton.addEventListener('click', acceptTerms);
    }

    var toggleButton = document.querySelector('[data-cutlist-toggle]');
    if (toggleButton) {
      toggleButton.addEventListener('click', function () {
        var isHidden = root.hasAttribute('hidden');
        if (isHidden) {
          root.removeAttribute('hidden');
          toggleButton.setAttribute('aria-expanded', 'true');
          showTerms();
        } else {
          root.setAttribute('hidden', '');
          toggleButton.setAttribute('aria-expanded', 'false');
        }
      });
    }

    var rowsBody = root.querySelector('[data-cutlist-rows]');
    var rowTemplate = document.querySelector('[data-cutlist-row-template]');
    var addRowButton = root.querySelector('[data-cutlist-add-row]');
    var addToCartButton = root.querySelector('[data-cutlist-add-to-cart]');
    var checkboxEl = root.querySelector('[data-cutlist-checkbox]');
    var checkboxLabelEl = root.querySelector('[data-cutlist-checkbox-label]');
    var instructionsCheckboxEl = root.querySelector('[data-cutlist-instructions-checkbox]');
    var instructionsInputEl = root.querySelector('[data-cutlist-instructions-input]');
    var errorEl = root.querySelector('[data-cutlist-error]');
    var resultsEl = root.querySelector('[data-cutlist-results]');
    var sheetsEl = root.querySelector('[data-cutlist-sheets]');
    var summaryCutLengthEl = root.querySelector('[data-cutlist-summary-cutlength]');
    var summaryMachiningEl = root.querySelector('[data-cutlist-summary-machining]');
    var summaryTotalEl = root.querySelector('[data-cutlist-summary-total]');

    function addRow() {
      var fragment = rowTemplate.content.cloneNode(true);
      rowsBody.appendChild(fragment);
      refreshBadges();
      scheduleRecalculate();
    }

    function removeRow(button) {
      var row = button.closest('.cutlist__row');
      if (row) row.remove();
      refreshBadges();
      scheduleRecalculate();
    }

    function refreshBadges() {
      var rows = rowsBody.querySelectorAll('.cutlist__row');
      for (var i = 0; i < rows.length; i++) {
        var badge = badgeForIndex(i);
        var badgeEl = rows[i].querySelector('[data-cutlist-badge]');
        badgeEl.textContent = badge.letter;
        badgeEl.style.background = badge.color;
      }
    }

    function readRows() {
      var rows = rowsBody.querySelectorAll('.cutlist__row');
      var pieces = [];
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var width = parseFloat(row.querySelector('[data-cutlist-field="width"]').value);
        var height = parseFloat(row.querySelector('[data-cutlist-field="height"]').value);
        var qty = parseInt(row.querySelector('[data-cutlist-field="qty"]').value, 10);

        if (!(width > 0) || !(height > 0) || !(qty > 0)) continue;
        pieces.push({ width: width, height: height, qty: qty, badge: badgeForIndex(i) });
      }
      return pieces;
    }

    /**
     * Shows/hides the "Minimum Cut" message directly under each row's width
     * and height inputs, live as the customer types - a field only counts
     * as invalid once it has a real entered value below data.minCut, not
     * while it's still blank. Returns true if any row is currently invalid,
     * so calculate() can hold off packing/results until it's fixed.
     */
    function validateMinCut() {
      var rows = rowsBody.querySelectorAll('.cutlist__row');
      var message = (labels.minCutMessage || 'Minimum Cut {min}mm').replace('{min}', data.minCut);
      var hasInvalid = false;

      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var width = parseFloat(row.querySelector('[data-cutlist-field="width"]').value);
        var height = parseFloat(row.querySelector('[data-cutlist-field="height"]').value);
        var widthError = row.querySelector('[data-cutlist-field-error="width"]');
        var heightError = row.querySelector('[data-cutlist-field-error="height"]');

        var widthInvalid = width > 0 && width < data.minCut;
        var heightInvalid = height > 0 && height < data.minCut;

        widthError.hidden = !widthInvalid;
        widthError.textContent = widthInvalid ? message : '';
        heightError.hidden = !heightInvalid;
        heightError.textContent = heightInvalid ? message : '';

        if (widthInvalid || heightInvalid) hasInvalid = true;
      }

      return hasInvalid;
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      resultsEl.hidden = true;
      sheetsEl.innerHTML = '';
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function formatGBP(amount) {
      return '£' + amount.toFixed(2);
    }

    function scheduleRecalculate() {
      lastResult = null;
      addToCartButton.disabled = true;
      clearTimeout(recalcTimer);
      recalcTimer = setTimeout(calculate, 250);
    }

    function calculate() {
      clearError();

      var hasInvalidCuts = validateMinCut();
      addRowButton.disabled = hasInvalidCuts;

      var pieces = readRows();
      if (pieces.length === 0 || hasInvalidCuts) {
        sheetsEl.innerHTML = '';
        resultsEl.hidden = true;
        return;
      }

      var variant = variantsById[currentVariantId];
      if (!variant) {
        showError('Please select a valid variant before calculating a cut list.');
        return;
      }

      var packResult = packPieces(pieces, variant.length, variant.width, data.kerf);
      if (packResult.error === 'piece-too-large') {
        var p = packResult.piece;
        showError('A part (' + Math.round(p.w) + '×' + Math.round(p.h) + 'mm) is larger than the sheet (' + variant.length + '×' + variant.width + 'mm), even rotated.');
        return;
      }

      var layout = { thickness: variant.thickness, sheets: packResult.sheets };
      var machiningResult = window.MachiningCharge.computeMachiningCharge(layout, data.machiningPricing, window.MachiningChargeTable);

      if (!machiningResult) {
        showError('Could not calculate the machining charge for this cut list. Please check the sizes entered.');
        return;
      }

      lastResult = {
        variant: variant,
        pieces: pieces,
        layout: layout,
        machiningResult: machiningResult,
      };

      renderResults(lastResult);
      addToCartButton.disabled = false;
    }

    function renderResults(result) {
      sheetsEl.innerHTML = '';

      for (var i = 0; i < result.layout.sheets.length; i++) {
        var sheet = result.layout.sheets[i];

        var card = document.createElement('div');
        card.className = 'cutlist__sheet';

        var title = document.createElement('p');
        title.className = 'cutlist__sheet-title';
        title.innerHTML =
          (labels.sheet || 'Sheet') + ' ' + (i + 1) + ' <span>· ' + Math.round(sheet.width) + '×' + Math.round(sheet.height) + 'mm</span>';
        card.appendChild(title);

        var meta = document.createElement('p');
        meta.className = 'cutlist__sheet-meta';
        meta.innerHTML =
          '<strong>' + sheetUsedPercent(sheet) + '% ' + (labels.used || 'used') + '</strong> · ' + sheet.pieces.length + ' ' + (labels.parts || 'parts');
        card.appendChild(meta);

        card.appendChild(renderSheetSvg(sheet, labels.offcut || 'Offcut'));
        sheetsEl.appendChild(card);
      }

      var machiningGBP = result.machiningResult.chargeGBPRounded;

      summaryCutLengthEl.textContent = 'Total cut length: ' + Math.round(result.machiningResult.cutLength) + 'mm';
      summaryMachiningEl.textContent = 'Machining charge: ' + formatGBP(machiningGBP);
      summaryTotalEl.textContent =
        'Total with cuts: ' + formatGBP(result.layout.sheets.length * (result.variant.price / 100) + machiningGBP);

      resultsEl.hidden = false;
    }

    /**
     * The cut list text for one physical sheet, grouping its placed pieces
     * by badge letter and looking each letter's width/height back up from
     * the original entered rows (not the placed piece) so the figures match
     * what the customer typed even if that piece got rotated to fit.
     */
    function buildSheetCutlistText(sheet, originalPieces) {
      var counts = {};
      var order = [];
      for (var i = 0; i < sheet.pieces.length; i++) {
        var letter = sheet.pieces[i].badge.letter;
        if (!(letter in counts)) {
          counts[letter] = 0;
          order.push(letter);
        }
        counts[letter]++;
      }

      return order
        .map(function (letter) {
          var original = originalPieces.filter(function (p) {
            return p.badge.letter === letter;
          })[0];
          return counts[letter] + '× ' + letter + ' ' + Math.round(original.width) + '×' + Math.round(original.height) + 'mm';
        })
        .join(', ');
    }

    /**
     * The full 'Cut list' cart property text: one "Sheet X of N: ..." group
     * per physical sheet, so it's clear which cuts come from which sheet
     * even though they all share one cart line - joined with " - " between
     * sheets so it stays readable as a single line of text.
     */
    function buildCutlistSummaryText(sheets, originalPieces) {
      var sheetLabel = labels.sheet || 'Sheet';
      return sheets
        .map(function (sheet, index) {
          return sheetLabel + ' ' + (index + 1) + ' of ' + sheets.length + ': ' + buildSheetCutlistText(sheet, originalPieces);
        })
        .join(' - ');
    }

    function addToCart() {
      if (!lastResult) {
        showError('Please wait for the cut list to finish calculating before adding it to the cart.');
        return;
      }

      addToCartButton.disabled = true;

      var ref = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var sheetsRequired = lastResult.layout.sheets.length;

      var checkboxLabel = checkboxLabelEl ? checkboxLabelEl.textContent.trim() : 'Include offcuts?';
      var checkboxValue = checkboxEl && checkboxEl.checked ? 'Yes' : 'No';

      var items = [
        {
          id: lastResult.variant.id,
          quantity: sheetsRequired,
          properties: {
            'Cut list': buildCutlistSummaryText(lastResult.layout.sheets, lastResult.pieces),
            _cutlist_layout: JSON.stringify(lastResult.layout),
            _cutlist_ref: ref,
            _cutlist_role: 'sheet',
            _cutlist_min_qty: String(sheetsRequired),
          },
        },
      ];

      if (checkboxLabel) {
        items[0].properties[checkboxLabel] = checkboxValue;
      }

      if (instructionsCheckboxEl && instructionsCheckboxEl.checked && instructionsInputEl) {
        var instructionsValue = instructionsInputEl.value.trim();
        if (instructionsValue) {
          var instructionsLabel = (labels.instructionsProperty || 'Additional instructions').trim();
          items[0].properties[instructionsLabel] = instructionsValue;
        }
      }

      var machiningItems = lastResult.machiningResult.cartItems;
      var totalQuantityAdded = sheetsRequired;
      for (var i = 0; i < machiningItems.length; i++) {
        items.push({
          id: machiningItems[i].id,
          quantity: machiningItems[i].quantity,
          properties: { _cutlist_ref: ref, _cutlist_role: 'machining' },
        });
        totalQuantityAdded += machiningItems[i].quantity;
      }

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (body) {
              throw new Error(body.description || body.message || 'Could not add the cut list to the cart.');
            });
          }
          return response.json();
        })
        .then(function () {
          root.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { quantity: totalQuantityAdded, button: addToCartButton },
            })
          );
        })
        .catch(function (err) {
          showError(err.message);
        })
        .finally(function () {
          addToCartButton.disabled = false;
        });
    }

    addRowButton.addEventListener('click', addRow);
    rowsBody.addEventListener('click', function (event) {
      if (event.target.closest('[data-cutlist-remove-row]')) removeRow(event.target);
    });
    rowsBody.addEventListener('input', scheduleRecalculate);
    addToCartButton.addEventListener('click', addToCart);

    if (instructionsCheckboxEl && instructionsInputEl) {
      instructionsCheckboxEl.addEventListener('change', function () {
        instructionsInputEl.hidden = !instructionsCheckboxEl.checked;
        if (!instructionsCheckboxEl.checked) {
          instructionsInputEl.value = '';
          instructionsInputEl.style.height = '';
        } else {
          resizeInstructionsInput();
        }
      });
      instructionsInputEl.addEventListener('input', resizeInstructionsInput);
    }

    function resizeInstructionsInput() {
      if (!instructionsInputEl) return;
      instructionsInputEl.style.height = 'auto';
      instructionsInputEl.style.height = instructionsInputEl.scrollHeight + 'px';
    }

    // Start with one empty row so the table isn't blank on open.
    addRow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose the packer for testing.
  window.CutlistPacker = { packPieces: packPieces, badgeForIndex: badgeForIndex };
})();
